import React, { useContext, useMemo } from "react";
import {
  matchRoutes,
  NavigationType,
  UNSAFE_LocationContext as LocationContext,
  UNSAFE_RouteContext as RouteContext,
} from "react-router-dom";

/** Single route used only to build a valid `matches` entry for `useParams()`. */
const EMBEDDED_PRODUCT_ROUTES = [
  { path: "/product/:productId", id: "__embedded_product__" },
];

function locationFromFullPath(fullPath) {
  const q = fullPath.indexOf("?");
  const pathname = q >= 0 ? fullPath.slice(0, q) : fullPath;
  const search = q >= 0 ? fullPath.slice(q) : "";
  return {
    pathname,
    search,
    hash: "",
    state: null,
    key: `embedded-${pathname}`,
  };
}

/**
 * Makes `ProductDetail` (and descendants) see the same `useParams` / `useLocation`
 * as if it were mounted at `fullPath`, without nesting a second `<Router>` under
 * the app `BrowserRouter`.
 */
export function EmbeddedProductRouteProviders({ fullPath, children }) {
  const parentLocationCtx = useContext(LocationContext);
  const parentRouteCtx = useContext(RouteContext);

  const syntheticLocation = useMemo(
    () => locationFromFullPath(fullPath),
    [fullPath]
  );

  const locationValue = useMemo(
    () => ({
      location: syntheticLocation,
      navigationType:
        parentLocationCtx?.navigationType ?? NavigationType.Pop,
    }),
    [parentLocationCtx?.navigationType, syntheticLocation]
  );

  const routeValue = useMemo(() => {
    const parentMatches = parentRouteCtx?.matches ?? [];
    const branchMatches = matchRoutes(
      EMBEDDED_PRODUCT_ROUTES,
      syntheticLocation.pathname
    );
    const extra =
      branchMatches?.length > 0
        ? branchMatches
        : [
            {
              params: {
                productId: String(
                  syntheticLocation.pathname.split("/").filter(Boolean).pop() ??
                    ""
                ),
              },
              pathname: syntheticLocation.pathname,
              pathnameBase: syntheticLocation.pathname,
              route: EMBEDDED_PRODUCT_ROUTES[0],
            },
          ];

    return {
      outlet: parentRouteCtx?.outlet ?? null,
      matches: [...parentMatches, ...extra],
    };
  }, [parentRouteCtx, syntheticLocation.pathname]);

  return (
    <RouteContext.Provider value={routeValue}>
      <LocationContext.Provider value={locationValue}>
        {children}
      </LocationContext.Provider>
    </RouteContext.Provider>
  );
}
